import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Paper,
  Stack,
  SvgIcon,
  SvgIconProps,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

/**
 * The assistant's own robot mark (design: robot-icon.svg) — antenna and side
 * "ears", not MUI's stock SmartToyOutlined glyph. `strokeWidth` follows the
 * design per usage: 2 at small, inline sizes (the Fab, the panel header, the
 * footnote badge) and 1.8 on the larger circular art (the announcement and
 * tour step 1).
 */
export const RobotIcon: React.FC<SvgIconProps & { strokeWidth?: number }> = ({ strokeWidth = 2, ...props }) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <g fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="8" width="16" height="12" rx="3" />
      <path d="M12 8V4" />
      <circle cx="12" cy="3" r="1" />
      <circle cx="9" cy="14" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="15" cy="14" r="1.2" fill="currentColor" stroke="none" />
      <path d="M1 13v4M23 13v4" />
    </g>
  </SvgIcon>
);

/**
 * Shared with ai-chat-widget.tsx's Fab ring (2s) and the announcement art's
 * ring below (2.4s) — only the `animation` shorthand's duration differs per
 * use, so one keyframes definition covers both.
 */
export const POM_PULSE_KEYFRAMES = {
  '0%': { transform: 'scale(1)', opacity: 0.55 },
  '100%': { transform: 'scale(1.9)', opacity: 0 },
};

const POM_FLOAT_KEYFRAMES = {
  '0%, 100%': { transform: 'translateY(0)' },
  '50%': { transform: 'translateY(-8px)' },
};

const REDUCE_MOTION_STILL = {
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
};

/**
 * First-run intro for the AI chat widget (design: Carlos, approved screens
 * "POM Safe Assistant — First-run Intro"). Three independent pieces, each
 * remembered per user in localStorage so it shows exactly once and stays
 * gone across reloads/navigations — same per-user-key pattern as
 * panelPositionStorageKey in ai-chat-widget.tsx.
 */

/**
 * Launch window for the announcement and nudge only — the one-time "here's
 * a new feature" push, not the tour or "Show the intro again" (those have no
 * end date). '2026-12-07T05:00:00Z' is midnight ET on Dec 7 (EST, UTC-5), so
 * both still show through end-of-day Dec 6. Move this out if the prod
 * release slips past that date.
 */
export const AI_CHAT_INTRO_NEW_UNTIL = '2026-12-07T05:00:00Z';

export type IntroPiece = 'announcement' | 'tour' | 'nudge';

export const INTRO_STORAGE_PREFIX = 'pomsafe.aiChat.intro';

export function introStorageKey(piece: IntroPiece, userId: string | undefined): string {
  const base = `${INTRO_STORAGE_PREFIX}.${piece}`;
  return userId ? `${base}.${userId}` : base;
}

function readIntroSeen(piece: IntroPiece, userId: string | undefined): boolean {
  try {
    return window.localStorage.getItem(introStorageKey(piece, userId)) === '1';
  } catch {
    return false;
  }
}

function writeIntroSeen(piece: IntroPiece, userId: string | undefined): void {
  try {
    window.localStorage.setItem(introStorageKey(piece, userId), '1');
  } catch {
    // Ignore storage write failures — the piece just won't stay dismissed across a reload.
  }
}

function clearIntroSeen(piece: IntroPiece, userId: string | undefined): void {
  try {
    window.localStorage.removeItem(introStorageKey(piece, userId));
  } catch {
    // Ignore storage write failures — the piece just won't stay "unseen" across a reload.
  }
}

export type TourStep = 1 | 2 | 3;

export interface AiChatIntroControls {
  showAnnouncement: boolean;
  dismissAnnouncement: () => void;
  showNudge: boolean;
  dismissNudge: () => void;
  showTour: boolean;
  tourStep: TourStep;
  tourNext: () => void;
  tourBack: () => void;
  tourSkip: () => void;
  showTourRestartLink: boolean;
  restartTour: () => void;
}

/**
 * Owns the seen/dismissed state for all three pieces. Doesn't know about the
 * chat panel's own open/close mechanics — the caller passes `panelOpen` in
 * and gets `showTour`/`showNudge` back already computed against it.
 *
 * Opening the panel (by any path — Try it now, or the Fab) permanently
 * retires the nudge ("until the user opens the chat once or dismisses the
 * label") and, if the tour hasn't been completed yet, (re)starts it at step
 * 1 — so a customer who opens, then minimizes before finishing, picks the
 * tour back up from the top on their next open rather than mid-step.
 */
export function useAiChatIntro(userId: string | undefined, panelOpen: boolean): AiChatIntroControls {
  const [announcementSeen, setAnnouncementSeen] = useState(() => readIntroSeen('announcement', userId));
  const [tourSeen, setTourSeen] = useState(() => readIntroSeen('tour', userId));
  const [nudgeSeen, setNudgeSeen] = useState(() => readIntroSeen('nudge', userId));
  const [tourStep, setTourStep] = useState<TourStep>(1);

  useEffect(() => {
    if (!panelOpen) return;
    if (!nudgeSeen) {
      writeIntroSeen('nudge', userId);
      setNudgeSeen(true);
    }
    if (!tourSeen) setTourStep(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelOpen]);

  const dismissAnnouncement = () => {
    writeIntroSeen('announcement', userId);
    setAnnouncementSeen(true);
  };
  const dismissNudge = () => {
    writeIntroSeen('nudge', userId);
    setNudgeSeen(true);
  };
  const tourSkip = () => {
    writeIntroSeen('tour', userId);
    setTourSeen(true);
  };
  const tourNext = () => {
    if (tourStep >= 3) {
      tourSkip();
      return;
    }
    setTourStep((step) => (step + 1) as TourStep);
  };
  const tourBack = () => setTourStep((step) => Math.max(1, step - 1) as TourStep);
  // "Show the intro again" (design: tour.markup.html line 24) — un-dismisses
  // the tour so showTour flips back on for the already-open panel.
  const restartTour = () => {
    clearIntroSeen('tour', userId);
    setTourSeen(false);
    setTourStep(1);
  };

  // The launch window gates the announcement/nudge outright — once it's
  // passed, both stay off regardless of what localStorage says (a customer
  // who never saw them doesn't get them late). The tour and "Show the intro
  // again" are untouched; they have no end date.
  const withinLaunchWindow = Date.now() < new Date(AI_CHAT_INTRO_NEW_UNTIL).getTime();

  return {
    showAnnouncement: !announcementSeen && withinLaunchWindow,
    dismissAnnouncement,
    showNudge: announcementSeen && !nudgeSeen && !panelOpen && withinLaunchWindow,
    dismissNudge,
    showTour: panelOpen && !tourSeen,
    tourStep,
    tourNext,
    tourBack,
    tourSkip,
    showTourRestartLink: tourSeen,
    restartTour,
  };
}

// Exported for reuse by ai-chat-escalation.tsx, which matches the same
// approved design system (colors, spacing) for the escalation/resolve UI.
export const ACCENT = '#2563A6';
export const ACCENT_SOFT_BG = '#E8F2FB';
const PANEL_NAVY = '#163F6E';
export const HEADING_INK = '#1F2A37';
export const BODY_INK = '#4A5968';

export const AI_CHAT_INTRO_ANNOUNCEMENT_BADGE = 'New in the Safety Hub';
export const AI_CHAT_INTRO_ANNOUNCEMENT_TITLE = 'Meet the POM Safe Assistant';
export const AI_CHAT_INTRO_ANNOUNCEMENT_BODY =
  'Your new help desk, built right into the Safety Hub. Ask it how any feature works and get an answer in seconds, and if you ever want a person, just say so and our support team joins the same chat.';
export const AI_CHAT_INTRO_ANNOUNCEMENT_CHECKLIST = [
  'Instant answers about invites, onboarding, devices and alerts',
  'Available 24/7, no ticket needed',
  'Hand off to a real human in the same conversation',
];
export const AI_CHAT_INTRO_TRY_IT_NOW = 'Try it now';
export const AI_CHAT_INTRO_MAYBE_LATER = 'Maybe later';
export const AI_CHAT_INTRO_FOOTNOTE_PREFIX = 'Look for the';
export const AI_CHAT_INTRO_FOOTNOTE_SUFFIX = 'button in the bottom-right corner, on every page.';
export const AI_CHAT_INTRO_SHOW_AGAIN = 'Show the intro again';

interface AnnouncementDialogProps {
  open: boolean;
  onTryItNow: () => void;
  onDismiss: () => void;
  // Caller-measured: ai-chat-widget.tsx's useZendeskStack reads Zendesk's
  // actual highest z-index from the DOM (re-measuring as it mounts/changes)
  // and passes one above it, since at narrow widths a Zendesk iframe
  // otherwise sits on top of this dialog and swallows clicks meant for its
  // buttons.
  zIndexAboveZendesk?: number;
}

const ANNOUNCEMENT_TITLE_ID = 'ai-chat-intro-announcement-title';

export const AnnouncementDialog: React.FC<AnnouncementDialogProps> = ({
  open,
  onTryItNow,
  onDismiss,
  zIndexAboveZendesk,
}) => (
  <Dialog
    open={open}
    onClose={onDismiss}
    maxWidth="md"
    fullWidth
    aria-labelledby={ANNOUNCEMENT_TITLE_ID}
    PaperProps={{ sx: { borderRadius: 4, overflowX: 'hidden', overflowY: 'auto', m: 2 } }}
    style={zIndexAboveZendesk !== undefined ? { zIndex: zIndexAboveZendesk } : undefined}
  >
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, position: 'relative' }}>
      <IconButton
        aria-label="Close"
        onClick={onDismiss}
        sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1, color: 'text.secondary' }}
      >
        <CloseIcon />
      </IconButton>

      <Box
        sx={{
          flexBasis: { sm: 280 },
          flexShrink: 0,
          minHeight: { xs: 180, sm: 'auto' },
          bgcolor: PANEL_NAVY,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          py: 4,
          // Short viewports (e.g. a POM Master in a small browser window) don't
          // have room for both the illustration and the text/buttons below it —
          // drop the illustration so the actionable content fits first.
          '@media (max-height: 500px)': { display: 'none' },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: 96,
            height: 96,
            animation: 'pomFloat 4s ease-in-out infinite',
            '@keyframes pomFloat': POM_FLOAT_KEYFRAMES,
            ...REDUCE_MOTION_STILL,
          }}
        >
          <Box
            component="span"
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              bgcolor: ACCENT,
              animation: 'pomPulse 2.4s ease-out infinite',
              '@keyframes pomPulse': POM_PULSE_KEYFRAMES,
              ...REDUCE_MOTION_STILL,
            }}
          />
          <Box
            sx={{
              position: 'relative',
              width: 96,
              height: 96,
              borderRadius: '50%',
              bgcolor: ACCENT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FFFFFF',
              boxShadow: '0 16px 40px rgba(0,0,0,0.3)',
            }}
          >
            <RobotIcon strokeWidth={1.8} sx={{ fontSize: 48 }} />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              right: -48,
              top: -8,
              bgcolor: '#FFFFFF',
              color: PANEL_NAVY,
              borderRadius: '14px 14px 14px 4px',
              px: 1.2,
              py: 0.7,
              fontSize: 12,
              fontWeight: 600,
              boxShadow: '0 8px 20px rgba(0,0,0,0.25)',
              whiteSpace: 'nowrap',
            }}
          >
            Hi there!
          </Box>
          <Box
            sx={{
              position: 'absolute',
              left: -56,
              bottom: 0,
              bgcolor: '#FFFFFF',
              color: PANEL_NAVY,
              borderRadius: '14px 14px 4px 14px',
              px: 1.2,
              py: 0.7,
              fontSize: 12,
              fontWeight: 600,
              boxShadow: '0 8px 20px rgba(0,0,0,0.25)',
              whiteSpace: 'nowrap',
            }}
          >
            How do I…?
          </Box>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#5BE38A' }} />
          <Typography
            sx={{ color: '#FFFFFF', fontSize: 12, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.85 }}
          >
            Online now
          </Typography>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          p: { xs: 3, sm: 5 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          '@media (max-height: 500px)': { p: 2.5, gap: 1.25 },
        }}
      >
        <Box
          sx={{
            alignSelf: 'flex-start',
            bgcolor: ACCENT_SOFT_BG,
            color: ACCENT,
            fontSize: 11.5,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            px: 1.4,
            py: 0.6,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 14 }} />
          {AI_CHAT_INTRO_ANNOUNCEMENT_BADGE}
        </Box>
        <Typography
          id={ANNOUNCEMENT_TITLE_ID}
          variant="h4"
          sx={{
            fontWeight: 600,
            color: HEADING_INK,
            fontSize: { xs: 24, sm: 32 },
            '@media (max-height: 500px)': { fontSize: 22 },
          }}
        >
          {AI_CHAT_INTRO_ANNOUNCEMENT_TITLE}
        </Typography>
        <Typography sx={{ color: BODY_INK, fontSize: 16, lineHeight: 1.55 }}>{AI_CHAT_INTRO_ANNOUNCEMENT_BODY}</Typography>
        <Stack spacing={1.25} sx={{ mt: 0.25 }}>
          {AI_CHAT_INTRO_ANNOUNCEMENT_CHECKLIST.map((line) => (
            <Stack key={line} direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  borderRadius: '50%',
                  bgcolor: ACCENT_SOFT_BG,
                  color: ACCENT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CheckIcon sx={{ fontSize: 16 }} />
              </Box>
              <Typography sx={{ fontSize: 14.5, color: HEADING_INK }}>{line}</Typography>
            </Stack>
          ))}
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1.25 }}>
          <Button
            variant="contained"
            onClick={onTryItNow}
            endIcon={<ArrowForwardIcon />}
            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT }, borderRadius: 2, py: 1.2, px: 2.5 }}
          >
            {AI_CHAT_INTRO_TRY_IT_NOW}
          </Button>
          <Button onClick={onDismiss} sx={{ color: '#5B6B7C' }}>
            {AI_CHAT_INTRO_MAYBE_LATER}
          </Button>
        </Stack>
        <Typography sx={{ fontSize: 12.5, color: '#7A8794', display: 'flex', alignItems: 'center', gap: 1 }}>
          {AI_CHAT_INTRO_FOOTNOTE_PREFIX}
          <Box
            component="span"
            sx={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              bgcolor: ACCENT,
              color: '#FFFFFF',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <RobotIcon sx={{ fontSize: 12 }} />
          </Box>
          {AI_CHAT_INTRO_FOOTNOTE_SUFFIX}
        </Typography>
      </Box>
    </Box>
  </Dialog>
);

interface TourStepContent {
  icon: React.ReactNode;
  title: string;
  body: string;
  examples?: string[];
}

const TOUR_STEPS: Record<TourStep, TourStepContent> = {
  1: {
    icon: <RobotIcon strokeWidth={1.8} sx={{ fontSize: 40, color: ACCENT }} />,
    title: 'Meet your POM Safe Assistant',
    body: 'Ask how anything in the Safety Hub works, from invite codes and onboarding to devices and alerts, and get an answer right here in seconds.',
  },
  2: {
    icon: <ChatBubbleOutlineIcon sx={{ fontSize: 40, color: ACCENT }} />,
    title: 'Just ask in plain English',
    body: 'No need to dig through help docs. Try something like:',
    examples: ['“How do I add a new user?”', '“How can I create a team?”', '“How do I schedule an automated reminder?”'],
  },
  3: {
    icon: <SupportAgentIcon sx={{ fontSize: 40, color: ACCENT }} />,
    title: 'Need a person? Just say so',
    body: 'Type “talk to a human” at any time and our support team joins this same chat. No new ticket, no new window.',
  },
};

export const AI_CHAT_TOUR_STEP_TEXT = {
  1: TOUR_STEPS[1],
  2: TOUR_STEPS[2],
  3: TOUR_STEPS[3],
};

interface TourOverlayProps {
  step: TourStep;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

export const TourOverlay: React.FC<TourOverlayProps> = ({ step, onNext, onBack, onSkip }) => {
  const content = TOUR_STEPS[step];
  const isLast = step === 3;
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        bgcolor: 'rgba(16,34,54,0.62)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        zIndex: 2,
      }}
    >
      <Paper elevation={0} sx={{ width: '100%', borderRadius: 3, p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: ACCENT }}>
            Step {step} of 3
          </Typography>
          <IconButton aria-label="Close intro" size="small" onClick={onSkip}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Box sx={{ alignSelf: 'center', width: 88, height: 88, borderRadius: '50%', bgcolor: ACCENT_SOFT_BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {content.icon}
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'center' }}>
          {content.title}
        </Typography>
        <Typography sx={{ fontSize: 14.5, color: BODY_INK, textAlign: 'center' }}>{content.body}</Typography>
        {content.examples && (
          <Stack spacing={1}>
            {content.examples.map((example) => (
              <Box
                key={example}
                sx={{
                  border: '1.5px solid #CFE2F3',
                  bgcolor: '#F6FAFE',
                  borderRadius: 2,
                  px: 1.75,
                  py: 1.25,
                  fontSize: 13.5,
                  fontWeight: 500,
                  color: HEADING_INK,
                }}
              >
                {example}
              </Box>
            ))}
          </Stack>
        )}
        {isLast && (
          <Stack spacing={1}>
            <Box sx={{ alignSelf: 'flex-end', maxWidth: '70%', bgcolor: ACCENT, color: '#FFFFFF', borderRadius: '14px 14px 4px 14px', px: 1.6, py: 1, fontSize: 13 }}>
              Can I talk to a human?
            </Box>
            <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center">
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#1E9E5A' }} />
              <Typography sx={{ fontSize: 11.5, fontWeight: 500, color: '#5B6B7C' }}>POM Safe Support joined the chat</Typography>
            </Stack>
            <Box sx={{ alignSelf: 'flex-start', maxWidth: '70%', bgcolor: '#F3F5F8', color: HEADING_INK, borderRadius: '14px 14px 14px 4px', px: 1.6, py: 1, fontSize: 13 }}>
              Hi, this is the support team. How can we help?
            </Box>
          </Stack>
        )}

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.5 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            {([1, 2, 3] as TourStep[]).map((dot) => (
              <Box
                key={dot}
                sx={{
                  width: dot === step ? 22 : 8,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: dot === step ? ACCENT : '#CBD5E1',
                  transition: 'all 0.2s',
                }}
              />
            ))}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {step === 1 && (
              <Button onClick={onSkip} sx={{ color: '#5B6B7C' }}>
                Skip
              </Button>
            )}
            {step > 1 && (
              <Button onClick={onBack} sx={{ color: '#5B6B7C' }}>
                Back
              </Button>
            )}
            <Button
              variant="contained"
              onClick={onNext}
              endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
              sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT }, borderRadius: 2 }}
            >
              {isLast ? 'Start chatting' : 'Next'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
};

export const AI_CHAT_NUDGE_BADGE = 'New';
export const AI_CHAT_NUDGE_TITLE = 'Questions? Ask the Assistant';
export const AI_CHAT_NUDGE_BODY = 'Instant answers about any feature, or reach our support team.';

interface NudgeLabelProps {
  bottomPx: number;
  fabSizePx: number;
  rightPx: number;
  gapPx: number;
  onDismiss: () => void;
}

/**
 * Centered on the Fab via `bottom: fabBottomPx + fabSizePx / 2` plus
 * `translateY(50%)` on the outer box — not a fixed-height flex row, since a
 * two-line title (long org names, browser zoom) would clip inside that and
 * overflow past the bottom of the screen. Still follows fabBottomPx exactly,
 * including while the Fab is stacked above the Zendesk launcher.
 *
 * The translateY is itself capped at `bottomPx + fabSizePx/2 - 16` (QA,
 * #553): for a POM Master at 400px wide the Fab sits only 20px from the
 * bottom, so centering a two-line label on it would push the label's own
 * bottom edge past the viewport. When there's room the cap is above 50% and
 * does nothing; when there isn't, it stops the label 16px above the bottom
 * instead of fully centered.
 */
export const NudgeLabel: React.FC<NudgeLabelProps> = ({ bottomPx, fabSizePx, rightPx, gapPx, onDismiss }) => {
  const horizontalOffsetPx = rightPx + fabSizePx + gapPx;
  return (
    <Box
      sx={{
        position: 'fixed',
        right: horizontalOffsetPx,
        bottom: bottomPx + fabSizePx / 2,
        transform: `translateY(min(50%, ${bottomPx + fabSizePx / 2 - 16}px))`,
        zIndex: (theme) => theme.zIndex.speedDial,
        transition: 'bottom 200ms ease',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderRadius: '14px',
          border: '1px solid #DCE3EA',
          boxShadow: '0 10px 28px rgba(16,34,54,0.14)',
          padding: '12px 10px 12px 16px',
          // Caps the width both at the design's own size (380px) and at
          // whatever actually fits beside the Fab at this viewport width —
          // without the second term, the label runs off the left edge at
          // narrow widths (the Fab's own offset leaves ~312px at 400px wide).
          maxWidth: `min(380px, calc(100vw - ${horizontalOffsetPx + 16}px))`,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                bgcolor: ACCENT_SOFT_BG,
                color: ACCENT,
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                px: 0.9,
                py: 0.25,
                borderRadius: 0.75,
                flexShrink: 0,
              }}
            >
              {AI_CHAT_NUDGE_BADGE}
            </Box>
            {/* Nowrap only from sm up — on a narrow screen the label is
                already width-capped above, so a one-line title there would
                force the Paper wider than the viewport instead of wrapping. */}
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: HEADING_INK,
                whiteSpace: { xs: 'normal', sm: 'nowrap' },
              }}
            >
              {AI_CHAT_NUDGE_TITLE}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: 12, color: '#5B6B7C' }}>{AI_CHAT_NUDGE_BODY}</Typography>
        </Box>
        <IconButton aria-label="Dismiss" size="small" onClick={onDismiss} sx={{ flexShrink: 0 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
        {/* Tail pointing at the Fab (design: nudge.markup.html line 32). */}
        <Box
          sx={{
            position: 'absolute',
            right: -7,
            top: '50%',
            mt: '-6px',
            width: 12,
            height: 12,
            bgcolor: '#FFFFFF',
            borderRight: '1px solid #DCE3EA',
            borderTop: '1px solid #DCE3EA',
            transform: 'rotate(45deg)',
          }}
        />
      </Paper>
    </Box>
  );
};
